import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <Helmet>
        <title>Terms of Service | Forecast</title>
        <meta name="description" content="Read the terms of service for Forecast, the budget calendar app." />
        <link rel="canonical" href="https://tryforecast.app/terms-of-service" />
      </Helmet>

      <div style={styles.container}>
        <button style={styles.backButton} onClick={() => navigate(-1)}>
          <span style={styles.backArrow}>&larr;</span> Back
        </button>

        <div style={styles.header}>
          <div style={styles.badge}>Legal</div>
          <h1 style={styles.title}>Terms of Service</h1>
          <p style={styles.date}>Effective March 6, 2025</p>
        </div>

        <div style={styles.divider} />

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>1. Introduction</h2>
          <p style={styles.text}>
            Welcome to Forecast ("App"). By downloading, accessing, or using our mobile application, you agree to be bound by these Terms of Service ("Terms"). Please read these Terms carefully before using the App.
          </p>
          <p style={styles.text}>
            If you do not agree with any part of these Terms, you may not access or use our App.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>2. Definitions</h2>
          <ul style={styles.list}>
            <li style={styles.listItem}>"App" refers to the Forecast mobile application.</li>
            <li style={styles.listItem}>"Service" refers to the features and functionality provided by the App.</li>
            <li style={styles.listItem}>"User" or "you" refers to the individual accessing or using the App.</li>
            <li style={styles.listItem}>"Content" refers to all information and data available through the App, including text, images, and financial data.</li>
            <li style={styles.listItem}>"Subscription" refers to the paid premium access to additional features of the App.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>3. Account Registration</h2>
          <p style={styles.text}>
            To use certain features of the App, you must create an account. You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete.
          </p>
          <p style={styles.text}>
            You are responsible for safeguarding your password and for all activities that occur under your account. You agree to immediately notify us of any unauthorized use of your account.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>4. Subscription Terms</h2>
          <p style={styles.text}>Forecast offers the following subscription options:</p>
          <div style={styles.highlightBox}>
            <p style={styles.highlightItem}><strong>Monthly</strong> &mdash; $6.70/month, auto-renews monthly</p>
            <p style={{ ...styles.highlightItem, marginBottom: 0 }}><strong>Yearly</strong> &mdash; $53.99/year ($4.50/month), auto-renews annually</p>
          </div>
          <p style={{ ...styles.text, marginTop: 14 }}>Subscription details:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Payment is processed through Stripe at confirmation of purchase.</li>
            <li style={styles.listItem}>Subscription automatically renews unless canceled before the end of the current period.</li>
            <li style={styles.listItem}>You can manage and cancel your subscription through the app or by contacting support.</li>
            <li style={styles.listItem}>Any unused portion of a free trial period will be forfeited when you purchase a subscription.</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>5. User Rights and Restrictions</h2>
          <h3 style={styles.subTitle}>5.1 License to Use</h3>
          <p style={styles.text}>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable license to download and use the App on a mobile device that you own or control for your personal, non-commercial use.
          </p>
          <h3 style={styles.subTitle}>5.2 Restrictions</h3>
          <p style={styles.text}>You agree not to:</p>
          <ul style={styles.list}>
            <li style={styles.listItem}>Modify, distribute, reverse engineer, or create derivative works of the App</li>
            <li style={styles.listItem}>Rent, lease, loan, sell, or transfer the App to any third party</li>
            <li style={styles.listItem}>Use the App for any illegal purpose or in violation of any local, state, national, or international law</li>
            <li style={styles.listItem}>Upload or transmit viruses, malware, or other malicious code</li>
            <li style={styles.listItem}>Interfere with or disrupt the integrity or performance of the App</li>
          </ul>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>6. Financial Information Disclaimer</h2>
          <p style={styles.text}>
            The App provides tools for financial planning and management. However, we do not provide financial advice, and the information provided through the App is for informational purposes only.
          </p>
          <p style={styles.text}>
            You should consult with a qualified financial professional before making any financial decisions. We are not responsible for any financial decisions you make based on the information provided through the App.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>7. Content and Data</h2>
          <p style={styles.text}>
            You retain all rights to the financial data you input into the App. By using the App, you grant us a license to use, store, and process your data solely for the purpose of providing and improving the Service.
          </p>
          <p style={styles.text}>
            We may use aggregated, anonymized data for analytical purposes and to improve the App's features and functionality.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>8. Termination</h2>
          <p style={styles.text}>
            We may terminate or suspend your access to the App immediately, without prior notice or liability, for any reason, including if you breach these Terms.
          </p>
          <p style={styles.text}>
            You may terminate your account at any time by following the instructions in the App or by contacting us. Termination of your account may not automatically cancel your subscription.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>9. Disclaimers</h2>
          <div style={styles.legalBox}>
            <p style={styles.legalText}>
              THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMISSIBLE UNDER APPLICABLE LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p style={{ ...styles.legalText, marginBottom: 0 }}>
              WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE CORRECTED, OR THAT THE APP IS FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>10. Limitation of Liability</h2>
          <div style={styles.legalBox}>
            <p style={styles.legalText}>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATED TO YOUR USE OF THE APP.
            </p>
            <p style={{ ...styles.legalText, marginBottom: 0 }}>
              OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING FROM OR RELATED TO THE APP SHALL NOT EXCEED THE AMOUNT YOU HAVE PAID TO US FOR THE APP IN THE LAST 12 MONTHS.
            </p>
          </div>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>11. Indemnification</h2>
          <p style={styles.text}>
            You agree to indemnify, defend, and hold harmless us, our officers, directors, employees, agents, and licensors from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your use of the App or your violation of these Terms.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>12. Changes to Terms</h2>
          <p style={styles.text}>
            We may modify these Terms at any time. The updated Terms will be effective immediately upon posting and will be accessible through the App. Your continued use of the App after any such changes constitutes your acceptance of the new Terms.
          </p>
          <p style={styles.text}>
            We encourage you to review the Terms regularly to stay informed about any updates.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>13. Governing Law</h2>
          <p style={styles.text}>
            These Terms shall be governed by and construed in accordance with the laws of the United States and the State of California, without regard to its conflict of law provisions.
          </p>
          <p style={styles.text}>
            Any dispute arising from or relating to these Terms or your use of the App shall be resolved exclusively in the state or federal courts located in California.
          </p>
        </section>

        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>14. Contact Information</h2>
          <p style={styles.text}>If you have questions about these Terms, please contact us at:</p>
          <div style={styles.contactCard}>
            <p style={styles.contactItem}>
              <span style={styles.contactLabel}>Email</span>
              <a href="mailto:r.dirawatun@gmail.com" style={styles.link}>r.dirawatun@gmail.com</a>
            </p>
            <p style={styles.contactItem}>
              <span style={styles.contactLabel}>Website</span>
              <a href="https://tryforecast.app" style={styles.link} target="_blank" rel="noopener noreferrer">tryforecast.app</a>
            </p>
          </div>
        </section>

        <div style={styles.footer}>
          <p style={styles.footerText}>Forecast &middot; San Diego, CA</p>
        </div>
      </div>
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: 'linear-gradient(180deg, #F8FAFC 0%, #EEF2FF 100%)',
    padding: '24px 16px 60px',
  },
  container: {
    maxWidth: 720,
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: 24,
    padding: '40px 36px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 32px rgba(0,0,0,0.06)',
  },
  backButton: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: 15,
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    color: '#6366F1',
    padding: '8px 0',
    marginBottom: 24,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    transition: 'opacity 0.2s',
  },
  backArrow: {
    fontSize: 18,
  },
  header: {
    textAlign: 'center',
    marginBottom: 8,
  },
  badge: {
    display: 'inline-block',
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 13,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#6366F1',
    background: 'rgba(99,102,241,0.08)',
    padding: '6px 16px',
    borderRadius: 20,
    marginBottom: 16,
  },
  title: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 800,
    fontSize: 36,
    color: '#0F172A',
    margin: '0 0 12px',
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  date: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 14,
    color: '#94A3B8',
    margin: 0,
  },
  divider: {
    height: 1,
    background: 'linear-gradient(90deg, transparent, #E2E8F0, transparent)',
    margin: '28px 0 36px',
  },
  section: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 700,
    fontSize: 20,
    color: '#1E293B',
    margin: '0 0 14px',
    letterSpacing: '-0.01em',
  },
  subTitle: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 600,
    fontSize: 17,
    color: '#334155',
    margin: '18px 0 10px',
  },
  text: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15.5,
    color: '#475569',
    lineHeight: 1.7,
    margin: '0 0 14px',
  },
  list: {
    margin: '0 0 14px',
    paddingLeft: 20,
  },
  listItem: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 15.5,
    color: '#475569',
    lineHeight: 1.7,
    marginBottom: 8,
  },
  highlightBox: {
    background: 'linear-gradient(135deg, #EEF2FF, #F0FDFA)',
    borderRadius: 16,
    padding: '20px 24px',
    marginTop: 12,
    borderLeft: '3px solid #6366F1',
  },
  highlightItem: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 500,
    fontSize: 15.5,
    color: '#1E293B',
    lineHeight: 1.6,
    margin: '0 0 8px',
  },
  legalBox: {
    background: '#F8FAFC',
    borderRadius: 16,
    padding: '20px 24px',
    border: '1px solid #E2E8F0',
  },
  legalText: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 13.5,
    color: '#64748B',
    lineHeight: 1.7,
    margin: '0 0 12px',
  },
  contactCard: {
    background: '#F8FAFC',
    borderRadius: 16,
    padding: '20px 24px',
    marginTop: 12,
  },
  contactItem: {
    fontFamily: "'Outfit', sans-serif",
    fontSize: 15,
    color: '#475569',
    margin: '0 0 10px',
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  contactLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: '#94A3B8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  link: {
    color: '#6366F1',
    textDecoration: 'none',
    fontWeight: 500,
  },
  footer: {
    textAlign: 'center',
    marginTop: 40,
    paddingTop: 24,
    borderTop: '1px solid #F1F5F9',
  },
  footerText: {
    fontFamily: "'Outfit', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    color: '#CBD5E1',
    margin: 0,
  },
};

export default TermsOfService;
